"use client";

import Image from "next/image";
import { resolveTitleHeadingPair } from "@/blocks/titleHeadingPair";
import {
  useEffect,
  startTransition,
  useDeferredValue,
  useId,
  useMemo,
  useState,
  type CSSProperties,
} from "react";

type ClientItem = {
  id: string;
  organisation: string;
  domainEntries: string[];
  searchText: string;
  logo?: {
    logoUrl?: string;
    mobileHeight: number;
    tabletHeight: number;
    desktopHeight: number;
    linkUrl?: string | null;
  } | null;
};

type ClientGroup = {
  letter: string;
  clients: ClientItem[];
};

type SortDirection = "asc" | "desc";

type ClientsBlockFields = {
  title?: string;
  subtitle?: string;
  titleHeadingPair?: string;
  order?: SortDirection;
  showLogo?: boolean;
  grayedOut?: boolean;
};

type ClientsListProps = {
  clients: ClientItem[];
} & ClientsBlockFields;

const getGroupLetter = (organisation: string): string => {
  const first = organisation.trim().charAt(0).toLocaleUpperCase("sv-SE");
  return /^[A-ZÅÄÖ]$/.test(first) ? first : "#";
};

const SWEDISH_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZÅÄÖ".split("");

const letterRank = (letter: string): number => {
  if (letter === "#") return 999;
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZÅÄÖ";
  const index = alphabet.indexOf(letter);
  return index === -1 ? 998 : index;
};

const compareClients = (
  a: ClientItem,
  b: ClientItem,
  direction: SortDirection,
): number => {
  const multiplier = direction === "desc" ? -1 : 1;
  const letterCompare =
    (letterRank(getGroupLetter(a.organisation)) -
      letterRank(getGroupLetter(b.organisation))) *
    multiplier;
  if (letterCompare !== 0) return letterCompare;
  return a.organisation.localeCompare(b.organisation, "sv-SE") * multiplier;
};

const getLetterAnchorId = (sectionId: string, letter: string): string =>
  `${sectionId}-letter-${letter === "#" ? "other" : letter.toLocaleLowerCase("sv-SE")}`;

const getDomainHref = (domain: string) =>
  /^[a-z][a-z0-9+.-]*:/i.test(domain) || domain.startsWith("//")
    ? domain
    : `https://${domain}`;

const INITIAL_VISIBLE_CLIENTS = 120;
const VISIBLE_CLIENTS_STEP = 120;

type LogoStyle = CSSProperties & {
  "--client-logo-mobile-height": string;
  "--client-logo-tablet-height": string;
  "--client-logo-desktop-height": string;
};

export function ClientsList({
  clients,
  order = "asc",
  title,
  subtitle,
  titleHeadingPair,
  showLogo = false,
  grayedOut = false,
}: ClientsListProps) {
  const { titleTag, titleClassName } = resolveTitleHeadingPair(titleHeadingPair);
  const TitleTag = titleTag;
  const sortDirection: SortDirection = order === "desc" ? "desc" : "asc";
  const [search, setSearch] = useState("");
  const [visibleState, setVisibleState] = useState(() => ({
    count: 0,
    key: "",
  }));
  const reactId = useId();
  const sectionId = `clients-list-${reactId.replace(/:/g, "")}`;
  const deferredSearch = useDeferredValue(search);
  const deferredQuery = deferredSearch.trim().toLocaleLowerCase("sv-SE");
  const searchActive = deferredQuery.length >= 2;

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => compareClients(a, b, sortDirection)),
    [clients, sortDirection],
  );

  const filteredClients = useMemo(() => {
    if (!searchActive) return sortedClients;

    return sortedClients.filter((client) => client.searchText.includes(deferredQuery));
  }, [deferredQuery, searchActive, sortedClients]);

  const visibleStateKey = useMemo(
    () =>
      `${sortDirection}:${searchActive ? deferredQuery : ""}:${clients.map((client) => client.id).join("|")}`,
    [clients, deferredQuery, searchActive, sortDirection],
  );
  const visibleCount =
    visibleState.key === visibleStateKey
      ? visibleState.count
      : Math.min(INITIAL_VISIBLE_CLIENTS, filteredClients.length);

  useEffect(() => {
    if (visibleCount >= filteredClients.length) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let idleId: number | undefined;

    const loadNextBatch = () => {
      if (cancelled) return;

      startTransition(() => {
        setVisibleState((current) => {
          const currentCount =
            current.key === visibleStateKey ? current.count : 0;

          return {
            key: visibleStateKey,
            count: Math.min(
              filteredClients.length,
              currentCount === 0
                ? INITIAL_VISIBLE_CLIENTS
                : currentCount + VISIBLE_CLIENTS_STEP,
            ),
          };
        });
      });
    };

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(loadNextBatch, { timeout: 120 });
    } else {
      timeoutId = globalThis.setTimeout(loadNextBatch, 16);
    }

    return () => {
      cancelled = true;

      if (typeof idleId === "number" && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }

      if (typeof timeoutId === "number") {
        globalThis.clearTimeout(timeoutId);
      }
    };
  }, [filteredClients.length, visibleCount, visibleState.key, visibleStateKey]);

  const visibleClients = useMemo(
    () => filteredClients.slice(0, visibleCount),
    [filteredClients, visibleCount],
  );

  const groups: ClientGroup[] = useMemo(() => {
    const groupedMap = new Map<string, ClientItem[]>();

    for (const client of visibleClients) {
      const letter = getGroupLetter(client.organisation);
      const existing = groupedMap.get(letter) ?? [];
      existing.push(client);
      groupedMap.set(letter, existing);
    }

    return Array.from(groupedMap.entries())
      .map(([letter, groupedClients]) => ({
        letter,
        clients: groupedClients,
      }))
      .sort((a, b) => {
        if (a.letter === "#" && b.letter !== "#") return -1;
        if (b.letter === "#" && a.letter !== "#") return 1;

        return sortDirection === "desc"
          ? letterRank(b.letter) - letterRank(a.letter)
          : letterRank(a.letter) - letterRank(b.letter);
      });
  }, [sortDirection, visibleClients]);

  const availableLetters = useMemo(
    () => new Set(groups.map((group) => group.letter)),
    [groups],
  );

  const orderedAlphabet = useMemo(
    () =>
      sortDirection === "desc"
        ? [...SWEDISH_ALPHABET].reverse()
        : SWEDISH_ALPHABET,
    [sortDirection],
  );

  const scrollToLetter = (letter: string) => {
    const element = document.getElementById(
      getLetterAnchorId(sectionId, letter),
    );
    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  if (!clients?.length) return null;

  const grayscaleStyles = grayedOut
    ? `
      .${sectionId} .partner-logo-image {
        filter: grayscale(100%);
        transition: filter 150ms ease;
      }
      .${sectionId} .partner-logo-wrapper:hover .partner-logo-image {
        filter: grayscale(0%);
      }
    `
    : "";

  const isProgressivelyLoading = visibleClients.length < filteredClients.length;
  const resultLabel = searchActive
      ? isProgressivelyLoading
        ? `Showing ${visibleClients.length} of ${filteredClients.length} matches`
        : `${filteredClients.length} matches`
      : isProgressivelyLoading
        ? `Showing ${visibleClients.length} of ${filteredClients.length} organisations`
        : `${filteredClients.length} organisations`;

  return (
    <div className={`${sectionId} flex flex-col gap-10 md:gap-12`}>
      <style>{grayscaleStyles}</style>
      {title || subtitle ? (
        <div className="section-title-gap-xl mb-10 max-w-4xl flex flex-col items-start">
          <div className="flex flex-col gap-1 md:gap-2">
            {title ? (
              <TitleTag
                className={`${titleClassName} text-foreground w-full text-balance`}
              >
                {title}
              </TitleTag>
            ) : null}
            {subtitle ? (
              <p className={`${titleClassName} text-muted-foreground w-full`}>
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <label
              htmlFor="clients-search"
              className="text-foreground text-sm font-medium"
            >
              Search
            </label>
            <input
              id="clients-search"
              type="search"
              value={search}
              onChange={(event) => {
                const value = event.target.value;
                startTransition(() => {
                  setSearch(value);
                });
              }}
              placeholder="Organisation or Domain"
              className="border-border bg-background text-foreground rounded-sm border px-3 py-2 text-sm w-full sm:w-64"
            />
          </div>
          <p className="text-muted-foreground text-sm">{resultLabel}</p>
          <div className="text-muted-foreground flex flex-wrap items-end text-sm">
            {["#", ...orderedAlphabet].map((letter) => {
              const hasClients = availableLetters.has(letter);

              return (
                <span key={letter} className="inline-flex items-end">
                  {hasClients ? (
                    <a
                      href={`#${getLetterAnchorId(sectionId, letter)}`}
                      onClick={(event) => {
                        event.preventDefault();
                        scrollToLetter(letter);
                      }}
                      className="text-foreground hover:text-foreground underline-offset-2 hover:underline px-1"
                    >
                      {letter}
                    </a>
                  ) : (
                    <span className="text-muted-foreground/50 underline-offset-2 px-1">
                      {letter}
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {groups.map((group) => (
        <div key={group.letter} className="flex flex-col gap-5">
          <h2
            id={getLetterAnchorId(sectionId, group.letter)}
            className="heading-lg"
          >
            {group.letter}
          </h2>
          <hr className="border-border" />
          <ul className="list-none pl-0! grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 lg:grid-cols-3">
            {group.clients.map((client) => {
              const linkHref = client.logo?.linkUrl;
              const logoUrl =
                typeof client.logo?.logoUrl === "string" &&
                client.logo.logoUrl.length > 0
                  ? client.logo.logoUrl
                  : null;
              const shouldRenderLogo = showLogo && Boolean(logoUrl);
              const content = (
                <>
                  {shouldRenderLogo ? (
                    <Image
                      src={logoUrl!}
                      alt={client.organisation}
                      title={client.organisation}
                      width={320}
                      height={128}
                      className="h-full max-h-16 w-auto object-contain"
                      loading="lazy"
                      unoptimized
                    />
                  ) : (
                    <p className="text-foreground text-base mb-0!">
                      {client.organisation}
                    </p>
                  )}
                  {client.domainEntries.length ? (
                    <div className="flex flex-col items-start gap-1">
                      {client.domainEntries.map((domain) => (
                        <a
                          key={domain}
                          href={getDomainHref(domain)}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-muted-foreground hover:text-foreground break-all"
                        >
                          {domain}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </>
              );

              return (
                <li
                  key={client.id}
                  className="partner-logo-wrapper list-none min-h-[var(--client-logo-mobile-height)] sm:min-h-[var(--client-logo-tablet-height)] lg:min-h-[var(--client-logo-desktop-height)]"
                  style={
                    {
                      "--client-logo-mobile-height": `${client.logo?.mobileHeight ?? 24}px`,
                      "--client-logo-tablet-height": `${client.logo?.tabletHeight ?? 32}px`,
                      "--client-logo-desktop-height": `${client.logo?.desktopHeight ?? 40}px`,
                    } as LogoStyle
                  }
                >
                  {linkHref ? (
                    <a
                      href={linkHref}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="partner-logo-image flex flex-col gap-1 justify-center items-start"
                      aria-label={client.organisation}
                    >
                      {content}
                    </a>
                  ) : (
                    <div className="partner-logo-image flex flex-col gap-1 justify-center items-start">
                      {content}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}

    </div>
  );
}
